import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LifeBuoy, Filter, AlertTriangle, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

const STATUS_COLORS = {
  open: "bg-emerald-100 text-emerald-700 border-emerald-300",
  in_review: "bg-blue-100 text-blue-700 border-blue-300",
  awaiting_user: "bg-amber-100 text-amber-700 border-amber-300",
  resolved: "bg-slate-100 text-slate-600 border-slate-300",
  closed: "bg-slate-100 text-slate-500 border-slate-300",
};
const PRIORITY_COLORS = { low: "text-slate-500", normal: "text-blue-600", high: "text-red-600" };

export default function AdminSupport() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_review: 0, resolved: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", priority: "", category: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.priority) params.priority = filter.priority;
      if (filter.category) params.category = filter.category;
      const { data } = await api.get("/admin/support/tickets", { params });
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const clearFilters = () => setFilter({ status: "", priority: "", category: "" });

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-8">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-800 text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <div className="hw-label text-blue-700">Admin · Support</div>
            <h1 className="hw-head text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-2 mt-1" data-testid="admin-support-heading">
              <LifeBuoy className="w-7 h-7 text-emerald-600" /> Support tickets
            </h1>
          </div>
          <div className="text-xs text-slate-500">Level-3 (SwaanStay-managed) tickets only. Level-1 (AI) & Level-2 (hotel chat) resolve upstream.</div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {["open", "in_review", "resolved", "closed"].map((k) => (
            <button key={k} type="button" onClick={() => setFilter({ ...filter, status: k })}
              className={`hw-card p-4 text-left transition ${filter.status === k ? "ring-2 ring-emerald-500" : "hover:shadow-md"}`}
              data-testid={`admin-support-count-${k}`}>
              <div className="hw-label text-slate-500 capitalize">{k.replace('_', ' ')}</div>
              <div className="hw-head text-3xl font-black text-slate-900 mt-1">{counts[k] ?? 0}</div>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="hw-card p-4 mt-4 flex flex-wrap items-end gap-3" data-testid="admin-support-filters">
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Filter className="w-3.5 h-3.5" /> Filter:
          </div>
          <select value={filter.priority} onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
            className="rounded-sm h-9 border border-slate-200 px-2 text-sm bg-white" data-testid="admin-support-priority-filter">
            <option value="">All priorities</option>
            <option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option>
          </select>
          <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            className="rounded-sm h-9 border border-slate-200 px-2 text-sm bg-white" data-testid="admin-support-category-filter">
            <option value="">All categories</option>
            <option value="booking">Booking</option><option value="payment">Payment</option>
            <option value="hotel">Hotel</option><option value="technical">Technical</option><option value="other">Other</option>
          </select>
          {(filter.status || filter.priority || filter.category) && (
            <button onClick={clearFilters} className="text-xs text-blue-700 hover:underline" data-testid="admin-support-clear-filters">Clear filters</button>
          )}
        </div>

        {/* List */}
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="hw-card p-6 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading tickets…</div>
          ) : tickets.length === 0 ? (
            <div className="hw-card p-8 text-center">
              <div className="hw-head text-lg font-black text-slate-700">No tickets match</div>
              <p className="text-sm text-slate-500 mt-2">Nothing pending under the current filters.</p>
            </div>
          ) : tickets.map((t) => (
            <button key={t.ticket_id} type="button" onClick={() => navigate(`/support/ticket/${t.ticket_id}`)}
              className="hw-card p-4 w-full text-left hover:hw-shadow-pop-amber transition-shadow flex items-start gap-3"
              data-testid={`admin-ticket-row-${t.ticket_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`hw-chip border ${STATUS_COLORS[t.status]} capitalize`}>{t.status.replace('_', ' ')}</span>
                  <span className={`text-xs font-bold uppercase ${PRIORITY_COLORS[t.priority] || ""}`}>{t.priority}</span>
                  <span className="text-xs text-slate-400 capitalize">· {t.category}</span>
                  {t.is_emergency && (
                    <span className="hw-chip border-red-300 bg-red-50 text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Emergency</span>
                  )}
                  <span className="hw-chip border-slate-200 bg-slate-50 text-slate-600 text-[10px]">{t.message_count} msgs</span>
                </div>
                <div className="hw-head font-black text-slate-900 mt-2 truncate">{t.subject}</div>
                <div className="hw-mono text-[11px] text-slate-500 mt-1">
                  {t.ticket_id} · {t.user_name} ({t.user_role}) · Opened {new Date(t.created_at).toLocaleString()}
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 mt-1" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
